/**
 * The list page's "Manage selected items" control.
 *
 * #238 replaced the native <select> this was built on with the designed menu
 * (Figma 19:5820 desktop, 19:11900 tablet, 19:14550 mobile drawer). Same
 * element, driven by `[data-option]` buttons instead of option values, plus
 * two rows the select never had: "Copy selected items to another list" and
 * "Select All".
 *
 * With nothing ticked the menu does not open at all (#992, Stuart 26.10.2026,
 * matching the live site): touching the trigger, or the Manage wishlist
 * menu's Manage Selected Items row below 1024, shows "Please select items
 * below" and the standalone Select all instead, at every width. The trigger
 * is still never disabled (#73 QA row 1), so the error has something to
 * answer. The per-row empty guards below stay as a backstop.
 *
 * Dan's QA on 21.08.2026 added three more:
 *
 * - Select All reads "Unselect All" once every rendered card is ticked, and
 *   clears the selection when it does.
 * - Bulk add-to-bag only attempts the cards it can buy. One sold-out variant
 *   used to 422 the whole /cart/add batch and lose every other line with it.
 * - The Copy to drawer paints its destinations instead of opening empty.
 *   Warmed on menu open here at first; since 04.09.2026 the drawer warms
 *   itself on page load (wishlist-copy-to.js), because the Manage wishlist
 *   menu's handoff row reaches the Copy row without opening this menu.
 *
 * The menu chrome (trigger toggle, outside click, Escape, aria, close
 * buttons, backdrop) is `ManageFunctions` (manage-functions.js, loaded by
 * the list page ahead of this file): the app renders the menu through the
 * shared `ManageMenu` component, so this class only owns the selection and
 * the options. Option rows carry
 * `data-wishlist-bulk-actions-el="option action-row"`: the base closes the
 * menu on `action-row` before `optionClicked` runs the option, and
 * `els.option` resolves by `*=` token match.
 */
class WishlistBulkActions extends window.BAO.ManageFunctions {
  connectedCallback () {
    super.connectedCallback()

    // A reload can restore ticked boxes, so the selection starts from what
    // is actually ticked rather than empty (#527, Codex plan check).
    this.itemIds = (this.externalEls.checkbox?.elements ?? [])
      .filter(box => box.checked)
      .map(box => box.value)
  }

  setupListeners () {
    super.setupListeners()

    this.optionClicked = this.optionClicked.bind(this)
    this.checkboxChanged = this.checkboxChanged.bind(this)
    this.checkboxMimicChanged = this.checkboxMimicChanged.bind(this)
    this.selectAllBoxChanged = this.selectAllBoxChanged.bind(this)

    // #338: the standalone Select all checkbox beneath the empty-selection
    // error (desktop/large-desktop only, CSS-gated).
    if (this.externalEls['select-all-box']?.exists) {
      this.listeners.add(
        this.externalEls['select-all-box'].element,
        'change',
        this.selectAllBoxChanged,
      )
    }

    this.listeners.add(this, 'click', this.optionClicked)

    // The error line is absolute, so the bar only reserves one line for it.
    // The out-of-stock message wraps to two or three lines at narrow widths
    // and ran under the first row of cards (Rosie, 05.11.2026). The observer
    // fires when the error shows, hides or rewraps, and hands the bar the
    // height past one line as --Actions_ErrorOverflow.
    const errorEl = this.externalEls?.error?.element
    if (errorEl) {
      this.errorResizeObserver = new ResizeObserver(() =>
        this.reserveErrorSpace(errorEl),
      )
      this.errorResizeObserver.observe(errorEl)
    }

    // Below 1024 the Manage wishlist menu (a separate <manage-functions>)
    // carries Manage Selected Items as a level; it asks before opening it.
    this.onManageDescend = this.onManageDescend.bind(this)
    this.listeners.add(document, 'manage-menu:descend', this.onManageDescend)

    // The card checkboxes and their popout mimics are painted by
    // wishlist-page-v2.js after this element connects. Delegating `change`
    // on the document binds them whatever the script order and survives a
    // re-render; the old per-element binding off a page-loaded event went
    // dead once this script left the theme head and registered after the
    // event had fired (Dan, 04.09.2026).
    this.onDocumentChange = this.onDocumentChange.bind(this)
    this.listeners.add(document, 'change', this.onDocumentChange)
  }

  onDocumentChange (e) {
    const external = e.target.getAttribute?.(this._externalElIdentifier)

    if (external === 'checkbox') this.checkboxChanged(e)
    if (external === 'checkbox-mimic') this.checkboxMimicChanged(e)
  }

  // #992: an empty selection never opens the menu; it explains itself.
  onTriggerClick (event) {
    if (!this.menuOpen && !this.itemIds.length) {
      event.preventDefault()
      this.refuseEmptySelection()
      return
    }

    super.onTriggerClick(event)
  }

  onManageDescend (event) {
    if (event.detail.level !== 'manage' || this.itemIds.length) return

    event.preventDefault()
    if (event.detail.via !== 'click') return

    event.target.closeMenu()
    this.refuseEmptySelection()
  }

  // The error and Select all sit in the toolbar's own padding, so the bar
  // is what comes into view (clear of the sticky header, its
  // scroll-margin-top): below 768 the customer has just come out of a
  // bottom drawer and may have scrolled while it was open.
  refuseEmptySelection () {
    this.showError(true)

    this.externalEls['actions-bar']?.element?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }

  // The base's onActionRowClick has already closed the menu by the time
  // this runs (registered first), so the option lands on a closed menu.
  optionClicked (e) {
    const option = e.target.closest(this.els?.option?.selector)
    if (!option) return

    this.runOption(option.dataset.option)
  }

  runOption (value) {
    switch (value) {
      case 'delete':
        this.deleteItems()
        break

      case 'add': {
        this.addItemsToCart()

        break
      }

      case 'copy': {
        this.copyItems()

        break
      }

      case 'copy-registry': {
        this.copyItems('registry-copy')

        break
      }

      case 'select-all': {
        this.selectAll()

        break
      }

      default:
        break
    }
  }

  /**
   * Ticks every card checkbox currently in the DOM. Pagination is dropped
   * (ADR-0023 load-all), so "every rendered card" is the whole list, and there
   * is no server-side "select all matching" concept to build.
   *
   * Ticks sold-out cards along with the rest: the designed row makes no
   * exception, and bulk add-to-bag already refuses a selection containing one
   * through its own banner (docs/qa/wishlist-bulk-actions-12-08.md rows 3-5).
   *
   * Updates the selection in ONE pass rather than firing a `change` per card.
   * `els`/`externalEls` re-query the whole document on every access, so routing
   * 300 cards through `checkboxChanged` cost 5.0s of document scans (measured
   * 20.08.2026, #238). 300 cards is an ordinary list under load-all
   * (ADR-0023), and 1024 is the ceiling. The card checkbox has no other
   * `change` listener to lose: `wishlist-item-quantity.js` only ever writes its
   * `data-quantity` (`:145-146`).
   *
   * The row reads "Unselect All" once every rendered card is ticked (#238 QA,
   * 21.08.2026) and clears the selection instead of re-ticking it, so the one
   * row covers both directions.
   */
  selectAll () {
    this.setAllSelected(
      !this.isAllSelected(this.externalEls.checkbox?.elements ?? []),
    )
  }

  // #338: the standalone Select all checkbox drives the same selection as
  // the dropdown row, so both call this rather than duplicating the pass
  // over the card checkboxes.
  selectAllBoxChanged (e) {
    this.setAllSelected(e.target.checked)
  }

  setAllSelected (checked) {
    const externalEls = this.externalEls
    const checkboxes = externalEls.checkbox?.elements ?? []
    const selected = new Set(this.itemIds)

    for (const checkbox of checkboxes) {
      checkbox.checked = checked

      if (checked) {
        selected.add(checkbox.value)
      } else {
        selected.delete(checkbox.value)
      }
    }

    for (const mimic of externalEls['checkbox-mimic']?.elements ?? []) {
      mimic.checked = checked
    }

    this.itemIds = [...selected]

    this.showError(false)
    this.syncSelectAllLabel(checkboxes)

    // #338: deselecting always hides the standalone checkbox, whichever
    // entry point drove it (the dropdown row or the box itself). Selecting
    // does NOT show it — only the empty-selection error does that
    // (`showError`) — so using the dropdown's Select All from outside the
    // error state can't summon the box, and ticking the box itself just
    // leaves it as it already was (visible, so it stays visible to be
    // unticked).
    if (!checked) this.hideSelectAll()
  }

  isAllSelected (checkboxes) {
    return checkboxes.length > 0 && checkboxes.length === this.itemIds.length
  }

  /**
   * Both labels ship in the markup and swap on `hidden`, the same two-label
   * pattern the drawer rows use, so the copy stays translatable rather than
   * being written into the JS.
   */
  syncSelectAllLabel (checkboxes = this.externalEls.checkbox?.elements ?? []) {
    const labels = this.els['select-all-label']
    if (!labels?.exists) return

    const all = this.isAllSelected(checkboxes)

    for (const label of labels.elements) {
      label.hidden = (label.dataset.view === 'unselect') !== all
    }

    const box = this.externalEls['select-all-box']?.element
    if (box) box.checked = all
  }

  /**
   * #338: the standalone checkbox is drawn only inside the empty-selection
   * error state (Figma 19:14703, 19:14847), not as permanent chrome. Exactly
   * one thing turns it ON — `showError` firing the empty-selection message —
   * so it can never appear from selecting cards by hand, only from actually
   * hitting the error. Several things turn it OFF: unticking it directly,
   * deselecting via the dropdown row, or ticking/unticking any ordinary
   * card (which also clears the error, same as it always did).
   */
  disconnectedCallback () {
    super.disconnectedCallback()
    this.errorResizeObserver?.disconnect()
  }

  reserveErrorSpace (errorEl) {
    const actionsBar = this.externalEls['actions-bar']?.element
    if (!actionsBar) return

    const lineHeight = parseFloat(getComputedStyle(errorEl).lineHeight) || 0
    const overflow = Math.max(0, errorEl.offsetHeight - lineHeight)
    actionsBar.style.setProperty('--Actions_ErrorOverflow', `${overflow}px`)
  }

  showSelectAll () {
    this.externalEls['actions-bar']?.element.classList.add(
      'wlh-Actions-hasSelectAll',
    )
  }

  hideSelectAll () {
    this.externalEls['actions-bar']?.element.classList.remove(
      'wlh-Actions-hasSelectAll',
    )
  }

  /**
   * Hands the current selection to the Copy to drawer (#238, registry mode
   * #119). The drawer owns every write from here: it lists the destinations
   * for the mode and posts one copy per row the customer picks. Nothing
   * about the source list changes, so there is no reload and no confirm
   * step.
   */
  copyItems (mode = 'lists') {
    if (!this.itemIds.length) {
      this.showError(true)
      return
    }

    this.showError(false)

    const drawer = this.externalEls?.['copy-to']?.element
    if (!drawer) return

    drawer.open(this.itemIds, mode)
  }

  async addItemsToCart () {
    if (!this.itemIds.length) {
      this.showError(true)
      return
    }

    // Only the purchasable cards are attempted (#238 QA, 21.08.2026). A
    // sold-out variant 422s the whole /cart/add batch, so one unavailable card
    // in the selection used to take every other line down with it (#73 QA row
    // 3, 12.08.2026). `data-purchasable` is the card's own add-to-bag branch
    // (`product-card-wishlist-v2.liquid`, `is_available`).
    const items = this.externalEls.checkbox.elements
      .filter(el => this.itemIds.includes(el.value))
      .filter(el => el.dataset.purchasable === 'true')
      .map(el => ({
        id: el.dataset.variantId,
        quantity: el.dataset.quantity,
        properties: { _Wishlist_ID: this.listId },
      }))

    if (!items.length) {
      this.showError(true, this.outOfStockErrorText)
      return
    }

    this.showError(false)

    await window.BAO.Cart.addItem(items)
  }

  checkboxChanged (e) {
    this.externalEls?.['checkbox-mimic']?.elements.forEach(c => {
      if (c.value === e.target.value) {
        c.checked = e.target.checked
      }
    })

    this.toggleItemId(e.target.value, e.target.checked)
  }

  checkboxMimicChanged (e) {
    this.externalEls.checkbox?.elements.forEach(c => {
      if (c.value === e.target.value) {
        c.checked = e.target.checked
      }
    })

    this.toggleItemId(e.target.value, e.target.checked)
  }

  toggleItemId (value, checked) {
    if (checked) {
      this.itemIds.push(value)
      this.showError(false)
    } else {
      this.itemIds = this.itemIds.filter(id => id !== value)
    }

    this.syncSelectAllLabel()

    // #338: an ordinary card, ticked or unticked by hand, is never the
    // reason the standalone checkbox is on screen — only the empty-selection
    // error is. Hiding it here unconditionally is what stops selecting every
    // card one at a time from summoning it with no error ever shown.
    this.hideSelectAll()
  }

  async deleteItems () {
    if (!this.itemIds.length) {
      this.showError(true)
      return
    }

    this.showError(false)

    window.BAO.wishlist.genericModalHeading = this.deleteModalHeading
    window.BAO.wishlist.genericModalText = this.deleteModalText
    window.BAO.wishlist.genericModalCancelText = this.deleteModalCancelText
    window.BAO.wishlist.genericModalContinueText = this.deleteModalContinueText
    window.BAO.wishlist.openGenericModal()

    window.BAO.wishlist.genericModalContinue = async () => {
      await window.BAO.wishlist.removeItems(
        this.listId,
        this.itemIds.join(','),
        this.token,
      )

      window.location.reload()
    }

    window.BAO.wishlist.genericModalCancel = async () => {
      window.BAO.wishlist.closeGenericModal()
    }
  }

  /**
   * One error line, two messages: the empty selection the bar has always had,
   * and the all-out-of-stock selection bulk add-to-bag can now refuse. Both
   * strings ride data attributes so the copy stays in the locale file.
   *
   * #338: the Select all checkbox is drawn only under the empty-selection
   * message (Figma 19:14703, 19:14847), never the out-of-stock one, so it
   * keys off `message === this.errorText` rather than `state` alone.
   * Codex review on PR #345: hitting the out-of-stock error while the
   * checkbox was already up from an earlier empty-selection error (tick
   * Select all, then bulk-add a selection that's gone all out of stock)
   * left it on screen under the wrong message — the empty-selection branch
   * has always turned it on, but nothing turned it off when a DIFFERENT
   * error replaced it, only when the error cleared entirely.
   */
  async showError (state, message = this.errorText) {
    const errorClass = 'wlh-Actions-hasError'
    const actionsBar = this.externalEls['actions-bar']
    const errorEl = this.externalEls?.error?.element

    if (errorEl && state) {
      errorEl.textContent = message
    }

    if (actionsBar) {
      if (
        (actionsBar.element.classList.contains(errorClass) && !state) ||
        (!actionsBar.element.classList.contains(errorClass) && state)
      ) {
        actionsBar.element.classList.toggle(errorClass)
      }
    }

    if (state && message === this.errorText) {
      this.showSelectAll()
    } else if (state) {
      this.hideSelectAll()
    }
  }

  get listId () {
    return this.dataset.listId
  }

  get errorText () {
    return this.dataset.errorText
  }

  get outOfStockErrorText () {
    return this.dataset.outOfStockErrorText
  }

  get token () {
    return window.BAO.wishlist.token
  }

  get deleteModalHeading () {
    return this.dataset.deleteModalHeading
  }

  get deleteModalText () {
    return this.dataset.deleteModalText
  }

  get deleteModalCancelText () {
    return this.dataset.deleteModalCancelText
  }

  get deleteModalContinueText () {
    return this.dataset.deleteModalContinueText
  }
}

customElements.define('wishlist-bulk-actions', WishlistBulkActions)
